import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '@/redux';
import { clearCredentials, AuthUser } from '@/redux/slices/authSlice';
import { clearPlan } from '@/redux/slices/planSlice';
import { onboardingActions } from '@/redux/slices/onboardingSlice';
import { authService } from '@/services/auth.service';
import { ROUTES } from '@/constants/routes';
import { hasAnyRole } from '@/utils/userRoles';

export type { AuthUser };
export type UserRole =
  | 'owner'
  | 'manager'
  | 'cashier'
  | 'inventory'
  | 'viewer'
  | 'b2b_sales'
   | 'b2b_sales_manager'
   | 'accountant'
   | 'employee_self_service'
   | 'shipper';

// Role hierarchy for permission checking
const ROLE_HIERARCHY: Record<UserRole, number> = {
  owner: 5,
  manager: 4,
  b2b_sales_manager: 3,
  accountant: 3,
  employee_self_service: 1,
  b2b_sales: 2,
  cashier: 2,
  inventory: 2,
  // Cùng bậc với thu ngân/thủ kho: nhân viên tuyến đầu, chỉ thao tác phần việc
  // của mình chứ không quản lý ai.
  shipper: 2,
  viewer: 1,
};

// Feature → allowed roles
const FEATURE_PERMISSIONS: Record<string, UserRole[]> = {
  pos: ['owner', 'manager', 'cashier'],
  inventory: ['owner', 'manager', 'inventory'],
  reports: ['owner', 'manager', 'viewer'],
  staff: ['owner', 'manager'],
  settings: ['owner'],
  orders: ['owner', 'manager', 'cashier', 'inventory', 'viewer'],
  products: ['owner', 'manager', 'inventory'],
  plan: ['owner'],
  b2b: ['owner', 'manager', 'b2b_sales', 'b2b_sales_manager', 'accountant', 'inventory'],
};

export const useAuth = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user) as AuthUser | null;
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  const logoutUser = useCallback(async () => {
    try { await authService.logout(); } catch { /* ignore */ }
    // clearCredentials đã xoá POS shift binding (pos.shiftStoreId + tenant tag) —
    // single source-of-truth, bao phủ cả logout lẫn token-expiry/401.
    dispatch(clearCredentials());
    dispatch(clearPlan());
    dispatch(onboardingActions.reset());
    navigate(ROUTES.auth.login, { replace: true });
  }, [dispatch, navigate]);

  // Role checking functions
  const hasRole = useCallback(
    (requiredRoles: UserRole[]) => {
      if (!user) return false;
      return hasAnyRole(user, requiredRoles);
    },
    [user]
  );

  const hasMinimumRole = useCallback(
    (minimumRole: UserRole) => {
      if (!user) return false;
      return ROLE_HIERARCHY[user.role] >= ROLE_HIERARCHY[minimumRole];
    },
    [user]
  );

  // Feature-based permission checking
  const canAccess = useCallback(
    (feature: keyof typeof FEATURE_PERMISSIONS) => {
      if (!user) return false;
      const requiredRoles = FEATURE_PERMISSIONS[feature];
      if (!requiredRoles) return false;
      return hasAnyRole(user, requiredRoles);
    },
    [user]
  );

  // Specific permission checks
  const canManageUsers = useMemo(() => hasRole(['owner', 'manager']), [hasRole]);
  const canManageInventory = useMemo(() => hasRole(['owner', 'manager', 'inventory']), [hasRole]);
  const canUsePOS = useMemo(() => hasRole(['owner', 'manager', 'cashier']), [hasRole]);
  const canViewReports = useMemo(() => hasRole(['owner', 'manager', 'viewer']), [hasRole]);
  const canManageSettings = useMemo(() => hasRole(['owner']), [hasRole]);
  const isOwner = useMemo(() => user?.role === 'owner', [user]);

  return {
    user,
    isAuthenticated,
    logout: logoutUser,
    hasRole,
    hasMinimumRole,
    canAccess,
    canManageUsers,
    canManageInventory,
    canUsePOS,
    canViewReports,
    canManageSettings,
    isOwner,
  };
};
